import * as React from 'react';
import { useLocation, useNavigate } from 'react-router';
import type { NotesPaging } from '../../notes/components/NotesPager';

const offsetParams = {
  Recived: 'notesReceivedPageOffset',
  Completed: 'notesCompletedPageOffset',
  Archived: 'notesArchivedPageOffset',
};

function readOffset(value: string | null): number {
  if (value === null || !/^\d+$/.test(value)) return 0;
  const offset = Number(value);
  return Number.isInteger(offset) && offset <= 2147483647 ? offset : 0;
}

export function useDocumentationNotesPaging() {
  const location = useLocation();
  const navigate = useNavigate();
  const currentLocation = React.useRef(location);
  React.useLayoutEffect(() => { currentLocation.current = location; }, [location]);

  const changeSearch = React.useCallback((update: (params: URLSearchParams) => void, replace: boolean) => {
    const current = currentLocation.current;
    const params = new URLSearchParams(current.search);
    update(params);
    const search = params.size > 0 ? `?${params.toString()}` : '';
    if (search === current.search) return;
    currentLocation.current = { ...current, search };
    navigate({ pathname: current.pathname, search, hash: current.hash }, { replace });
  }, [navigate]);

  const params = new URLSearchParams(location.search);
  const folderId = params.get('nodeId');
  const getPaging = (type: keyof typeof offsetParams): NotesPaging => ({
    offset: readOffset(params.get(offsetParams[type])),
    onOffsetChange: (offset, replace = false) => {
      const current = currentLocation.current;
      if (current.pathname !== location.pathname || new URLSearchParams(current.search).get('nodeId') !== folderId) return;
      changeSearch(next => {
        const key = offsetParams[type];
        next.set(key, String(offset));
      }, replace);
    },
  });

  const selectFolder = (id: number) => {
    changeSearch(next => {
      if (Number(next.get('nodeId')) !== id) {
        for (const key of Object.values(offsetParams)) next.delete(key);
      }
      next.set('nodeId', String(id));
    }, false);
  };

  return {
    getPaging,
    selectFolder,
    hasSavedPaging: Object.values(offsetParams).some(key => params.has(key)),
  };
}
